// hooks/usePriority.js
import { useState, useCallback } from "react";
import toast from "react-hot-toast";

export const usePriority = (
  apiUrl,
  refreshNotes,
  viewMode,
  hasActiveFilters,
  selectedFilters,
  fetchNotesWithFilters,
  fetchStackedJobs,
) => {
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [priorityDropdownPosition, setPriorityDropdownPosition] = useState({
    x: 0,
    y: 0,
  });
  const [selectedNoteForPriority, setSelectedNoteForPriority] = useState(null);

  const handlePriorityClick = useCallback((e, note) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setPriorityDropdownPosition({
      x: rect.right - 200,
      y: rect.bottom + 5,
    });
    setSelectedNoteForPriority(note);
    setShowPriorityDropdown(true);
  }, []);

  const handlePriorityChange = useCallback(
    async (priorityValue) => {
      if (!selectedNoteForPriority) return;
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        const noteId = selectedNoteForPriority.id;
        const updatePriority = async () => {
          try {
            const getRes = await fetch(
              `${apiUrl}/Priority/GetPriorityByNoteId/${noteId}`,
            );
            if (getRes.ok) {
              const priorityData = await getRes.json();
              const priorityId = priorityData.priority?.id;
              if (priorityId) {
                const putRes = await fetch(
                  `${apiUrl}/Priority/UpdatePriority/${priorityId}`,
                  {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ priorityValue }),
                  },
                );
                if (putRes.ok)
                  return {
                    success: true,
                    message: "Priority updated successfully",
                  };
              }
            }
          } catch (e) {
            console.log("No existing priority, will create new");
          }
          const postRes = await fetch(`${apiUrl}/Priority/AddPriority`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              noteID: noteId,
              priorityValue,
              userId: user.id,
            }),
          });
          if (postRes.ok)
            return { success: true, message: "Priority created successfully" };
          throw new Error("Failed to create priority");
        };
        const result = await updatePriority();
        if (result.success) {
          toast.success(result.message);
          await refreshNotes();
          if (viewMode === "stacked") {
            if (hasActiveFilters) {
              await fetchNotesWithFilters(selectedFilters);
            } else {
              await fetchStackedJobs();
            }
          }
        }
      } catch (error) {
        console.error("Error updating priority:", error);
        toast.error("Failed to update priority");
      } finally {
        setShowPriorityDropdown(false);
        setSelectedNoteForPriority(null);
      }
    },
    [
      selectedNoteForPriority,
      apiUrl,
      refreshNotes,
      viewMode,
      hasActiveFilters,
      selectedFilters,
      fetchNotesWithFilters,
      fetchStackedJobs,
    ],
  );

  const closePriorityDropdown = useCallback(() => {
    setShowPriorityDropdown(false);
    setSelectedNoteForPriority(null);
  }, []);

  return {
    showPriorityDropdown,
    priorityDropdownPosition,
    selectedNoteForPriority,
    handlePriorityClick,
    handlePriorityChange,
    closePriorityDropdown,
  };
};
